/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {HttpErrorResponse} from '@angular/common/http';
import {Component, Input, OnDestroy, ViewChild} from '@angular/core';

import {TranslateService} from '@ngx-translate/core';

import {EMPTY, Observable} from 'rxjs';
import {catchError, finalize, switchMap, takeUntil, tap} from 'rxjs/operators';

import {ToastrService} from 'ngx-toastr';
import {Destroyable} from '../../destroyable.component';
import {Image, ImageCropperComponent} from '../files';
import {FileDescription} from '../files/files.model';
import {ModalComponent, ModalService} from '../modal';
import {Files, getFileNameAndExtension} from '../utils/files';

export enum STATE {
  INITIAL_PREVIEW = 'INITIAL_PREVIEW',
  IMAGE_SELECTED_FOR_UPLOAD = 'IMAGE_SELECTED_FOR_UPLOAD',
  UPLOAD_IN_PROGRESS = 'UPLOAD_IN_PROGRESS',
  IMAGE_UPLOADED = 'IMAGE_UPLOADED',
  IMAGE_SELECTED_FOR_DELETE = 'IMAGE_SELECTED_FOR_DELETE',
  DELETE_IN_PROGRESS = 'DELETE_IN_PROGRESS',
  IMAGE_DELETED = 'IMAGE_DELETED',
}

@Component({
  selector: 'gp-image-management',
  templateUrl: './image-management.component.html',
  styleUrls: ['./image-management.component.scss'],
})
export class ImageManagementComponent extends Destroyable implements OnDestroy {

  public currentState: STATE | '' = STATE.INITIAL_PREVIEW;
  public imageForUpload: File;
  public photoLink: string;
  public isCropperEnabled: boolean;
  public imageSize: { width: number, height: number };
  public maxSizeError = null;
  public fileFormatError = null;
  public imgUrl: string | ArrayBuffer;
  private ALLOWED_EXTENSIONS = ['bmp', 'gif', 'jpeg', 'jpg', 'png', 'tiff', 'webp'];


  @ViewChild('cropper', {static: false})
  public cropper: ImageCropperComponent;

  @Input()
  public title: string;

  public imageSelectedCallback: ((img: File) => void);
  public imageSourceCallback: (() => Observable<Image>);
  public showImageOperationLoaderCallback: (() => void);
  public hideImageOperationLoaderCallback: (() => void);
  public imageUploadCallBack: ((img: Image) => Observable<FileDescription | unknown>);
  public imageUploadSuccessCallBack: (() => void);
  public imageDeleteSuccessCallBack: (() => void);
  public imageUploadCompletedCallBack: (() => void);

  public imageDeleteCallBack: (() => Observable<FileDescription | unknown>);

  constructor(private toastService: ToastrService,
              private modalService: ModalService,
              private translate: TranslateService) {
    super();
  }

  public get isInitialPreview(): boolean {
    return this.currentState === STATE.INITIAL_PREVIEW;
  }

  public get isImageSelectedForUpload(): boolean {
    return this.currentState === STATE.IMAGE_SELECTED_FOR_UPLOAD;
  }

  public get isImageSelectedForDelete(): boolean {
    return this.currentState === STATE.IMAGE_SELECTED_FOR_DELETE;
  }

  public get isImageSelectedForOperation(): boolean {
    return this.isImageSelectedForUpload || this.isImageSelectedForDelete;
  }

  public get isOperationInProgress(): boolean {
    return this.currentState === STATE.UPLOAD_IN_PROGRESS || this.currentState === STATE.DELETE_IN_PROGRESS;
  }

  public get hasImageToPreview(): boolean {
    return this.photoLink && !this.isImageSelectedForDelete;
  }

  public get isUploadEnabled(): boolean {
    return !this.isOperationInProgress && !this.isImageSelectedForDelete;
  }

  public get isDeleteEnabled(): boolean {
    return this.hasImageToPreview && !this.isImageSelectedForOperation && !this.isOperationInProgress;
  }

  public get isSubmitEnabled(): boolean {
    return this.isImageSelectedForDelete || (this.isImageSelectedForUpload && !this.maxSizeError && !this.fileFormatError);
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalService.modalClosed.emit('gp-image-management');
  }

  public onCancel(modal: ModalComponent): void {
    return modal.close();
  }

  public imageSelected(event: any): void {
    if (event.target.files.length > 0) {
      this.imageForUpload = event.target.files[0];
      this.currentState = STATE.IMAGE_SELECTED_FOR_UPLOAD;
      this.maxSizeError = null;
      this.fileFormatError = null;

      if (typeof this.imageSelectedCallback === 'function') {
        this.maxSizeError = this.imageSelectedCallback(this.imageForUpload);
      }

      if (!this.isValidFormat(this.imageForUpload.name)) {
        this.fileFormatError = this.translate.instant('FORMS.VALIDATORS.allowed_image_formats', {
          formats: this.ALLOWED_EXTENSIONS.join(', ')
        });
        this.maxSizeError = null;
      }

      if (!this.maxSizeError && !this.fileFormatError) {
        if (!this.isCropperEnabled) {
          this.getBase64StringFromBlob();
        }
      }
    }
  }

  public deleteImage(): void {
    if (typeof this.imageDeleteCallBack === 'function') {
      this.currentState = STATE.IMAGE_SELECTED_FOR_DELETE;
      this.imageDeleteCallBack();
    }
  }

  public onSubmit(): void {
    if (this.isImageSelectedForUpload) {
      this.handleUpload();
    } else if (this.isImageSelectedForDelete) {
      this.handleDelete();
    }
  }

  private handleUpload(): void {
    this.currentState = STATE.UPLOAD_IN_PROGRESS;
    let $imageSource: Observable<Image>;

    if (typeof this.imageSourceCallback === 'function') {
      const observable: Observable<Image> = this.imageSourceCallback();
      $imageSource = observable ? observable : this.cropper.getResult();
    } else {
      $imageSource = this.cropper.getResult();
    }

    $imageSource.pipe(
      switchMap((image: Image) => {
        if (typeof this.showImageOperationLoaderCallback === 'function') {
          this.showImageOperationLoaderCallback();
        }
        return this.imageUploadCallBack(image);
      }),
      tap(() => {
        if (typeof this.imageUploadSuccessCallBack === 'function') {
          this.imageUploadSuccessCallBack();
        }
        this.currentState = STATE.IMAGE_UPLOADED;
        this.modalService.closeTopmostModal();
        this.toastService.success(this.translate.instant(`FORMS.image_saved_successfully`));
      }),
      catchError((errorResponse: HttpErrorResponse) => {
        this.currentState = STATE.IMAGE_SELECTED_FOR_UPLOAD;
        const errorMessage = `${this.translate.instant(`FORMS.image_save_failed`)}: ${
          errorResponse?.error?.message
        }`;
        this.toastService.error(errorMessage);
        return EMPTY;
      }),
      finalize(() => {
        if (typeof this.hideImageOperationLoaderCallback === 'function') {
          this.hideImageOperationLoaderCallback();
        }
      }),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  private handleDelete(): void {
    this.currentState = STATE.DELETE_IN_PROGRESS;

    if (typeof this.showImageOperationLoaderCallback === 'function') {
      this.showImageOperationLoaderCallback();
    }

    if (typeof this.imageDeleteCallBack === 'function') {
      this.imageDeleteCallBack()
        .pipe(
          tap(() => {
            if (typeof this.imageDeleteSuccessCallBack === 'function') {
              this.imageDeleteSuccessCallBack();
            }
            this.currentState = STATE.IMAGE_DELETED;
            this.modalService.closeTopmostModal();
            this.toastService.success(this.translate.instant(`FORMS.image_deleted_successfully`));
          }),
          catchError((errorResponse: HttpErrorResponse) => {
            this.currentState = STATE.IMAGE_SELECTED_FOR_DELETE;

            const errorMessage = `${this.translate.instant(`FORMS.image_deletion_failed`)}: ${
              errorResponse?.error?.message
            }`;
            this.toastService.error(errorMessage);
            return EMPTY;
          }),
          finalize(() => {
            this.currentState = STATE.INITIAL_PREVIEW;
            if (typeof this.hideImageOperationLoaderCallback === 'function') {
              this.hideImageOperationLoaderCallback();
            }
          }),
          takeUntil(this.componentDestroyed$)
        )
        .subscribe();
    }
  }

  private getBase64StringFromBlob(): void {
    Files.blobToBase64(this.imageForUpload)
      .pipe(
        tap((base64String: string) => {
          this.imgUrl = base64String;
        })
      ).subscribe();
  }

  private isValidFormat(name: string): boolean {
    const fileExtension = getFileNameAndExtension(name).fileExtension.toLowerCase();
    return this.ALLOWED_EXTENSIONS.includes(fileExtension);
  }
}
